import { uniqueArray } from "@msuite/utils/array";
import { getBucketPath } from "@msuite/utils/files";
import type {
	DocumentStructure,
	DocumentStructureFileItem,
	DocumentStructureFolderItem,
	Nullable,
	Tables,
} from "@msuite/utils/types";
import React from "react";
import type { IFileExplorerContext } from "./context";

export type CheckState = "unchecked" | "checked" | "indeterminate";

/**
 * This function checks the state of a folder's items based on the selected files.
 * It returns "checked" if all files are selected, "unchecked" if none are selected
 * and "indeterminate" if some files are selected.
 * @param items
 * @param selectedFiles
 * @returns
 */
export function checkItemsState<T extends { file: Tables<"files"> }>(
	items: DocumentStructureFolderItem<T>["items"],
	selectedFiles: string[],
): CheckState {
	let hasSelected = false;
	let hasUnselected = false;

	for (const item of items) {
		if (item.type === "file") {
			if (selectedFiles.includes(item.file.id)) {
				hasSelected = true;
			} else {
				hasUnselected = true;
			}
		} else {
			const state = checkItemsState(item.items || [], selectedFiles);
			if (state === "checked") {
				hasSelected = true;
			} else if (state === "unchecked") {
				hasUnselected = true;
			} else if (state === "indeterminate") {
				hasSelected = true;
				hasUnselected = true;
			}
		}
	}

	if (hasSelected && hasUnselected) {
		return "indeterminate";
	} else if (hasSelected) {
		return "checked";
	} else {
		return "unchecked";
	}
}

/**
 * This function flattens a folder's items into a single array of file items.
 * It recursively traverses through folders and collects all file items.
 * It ensures that each file item is unique based on its ID.
 * @param items
 * @returns
 */
export function flattenItems<T extends { file: Tables<"files"> }>(
	items: Nullable<DocumentStructureFolderItem<T>["items"]>,
): DocumentStructureFileItem<T>[] {
	if (!items) return [];
	const flatItems: DocumentStructureFileItem<T>[] = [];
	for (const item of items) {
		if (item.type === "file") {
			if (flatItems.some((flatItem) => flatItem.file.id === item.file.id)) continue;
			flatItems.push(item);
		} else if (item.type === "folder" && item.items) {
			flatItems.push(...flattenItems(item.items));
		}
	}

	return flatItems;
}

/**
 * This function retrieves the IDs of items in a folder based on the current state.
 * It returns an array of IDs that should be selected based on the current state.
 * @param initialState
 * @param items
 * @returns
 */
export function handleBatchSelectedFiles<T extends { file: Tables<"files"> }>(
	initialState: CheckState,
	items: DocumentStructureFolderItem<T>["items"],
	currentSelectedFiles: string[],
): string[] {
	let selectedFiles = Array.from(currentSelectedFiles);

	if (initialState === "checked") {
		const flatItems = flattenItems(items);
		selectedFiles = selectedFiles.filter(
			(fileId) => !flatItems.some((item) => item.file.id === fileId),
		);
	} else {
		const flatItems = flattenItems(items);
		const newSelectedFiles = flatItems.map((item) => item.file.id);
		selectedFiles.push(...newSelectedFiles);
	}

	return uniqueArray(selectedFiles);
}

export function findLogestPath<T extends { file: Tables<"files"> }>(
	structure: Nullable<DocumentStructure<T>>,
	ids: string[],
): Nullable<string> {
	if (!structure?.items) return null;
	const flatItems = flattenItems(structure.items);
	const filteredItems = flatItems.filter((item) => ids.includes(item.file.id));
	if (filteredItems.length === 0) return null;
	const paths = filteredItems.map((item) => item.file.fullPath);
	const longestPath = paths.reduce((longest, current) => {
		const segments = current.split("/");
		return segments.length > longest.split("/").length ? current : longest;
	}, "");

	const path = getBucketPath(longestPath);
	return path.cleanPath;
}

/**
 * Whether a row-bound rule holds for every file in a selection.
 *
 * The selection is a list of ids and the rule needs rows, so the ids are resolved through the
 * explorer's unfiltered map — the rendered tree is narrowed by the search box while the selection
 * is not, and resolving against the rendered tree silently drops the files the search hid. An id
 * the map does not know fails: a batch that cannot be inspected is not a batch to allow.
 *
 * @param selectedFiles - The ids currently ticked.
 * @param filesById - Every file the explorer holds, from the tree before the search narrows it.
 * @param may - The row-bound rule to ask of each file.
 * @returns Whether the rule holds for the whole selection.
 */
export function selectionPasses(
	selectedFiles: string[],
	filesById: Map<string, Tables<"files">>,
	may: (file: Tables<"files">) => boolean,
): boolean {
	return selectedFiles.every((fileId) => {
		const file = filesById.get(fileId);
		return !!file && may(file);
	});
}

/**
 * The component an explorer's owner mounts at the head of the toolbar, for the actions only that
 * collection has. It is handed the explorer's context, so an action reads the selection through
 * `filesById` and refreshes the tree through `refetch` without the explorer knowing what it does.
 *
 * A component rather than a function returning nodes: the slot is filled with the component itself
 * (`menu={ImportOfferAction}`), and the explorer mounts it.
 */
export type FileExplorerMenuExtension = React.ComponentType<{
	context: IFileExplorerContext<{ file: Tables<"files"> }>;
}>;

/** One choice a select column offers: the value written, and the label the row shows for it. */
export interface FileExplorerSelectOption {
	value: string;
	label: string;
}

/**
 * A column of per-row select boxes, described by whoever owns the rows: what the column is called,
 * what it offers, where a row's current value comes from and what to do with a new one.
 *
 * The explorer knows nothing about the fields behind such a column — it reads and writes them
 * through `getValue` and `onChange` — so a collection adds a column by describing it rather than by
 * teaching the explorer about its table. Columns render between the name and the size, in the order
 * they are given.
 */
export interface FileExplorerSelectColumn<T extends { file: Tables<"files"> }> {
	/** Tells this column apart from the others; the React key of its header and of every cell. */
	key: string;
	/** The column heading, already localised. */
	header: string;
	options: FileExplorerSelectOption[];
	getValue: (item: DocumentStructureFileItem<T>) => string;
	onChange: (item: DocumentStructureFileItem<T>, value: string) => void | Promise<void>;
	/**
	 * Whether this row's value may be changed at all — a column that does not apply to every row
	 * leaves the cell empty rather than offering a box that writes something meaningless. Defaults
	 * to editable, and `canUpdateFile` is asked first either way.
	 */
	isEditable?: (item: DocumentStructureFileItem<T>) => boolean;
}
